import { Badge } from "@/lib/components/Badge";
import { Button } from "@/lib/components/Button/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { cn } from "@/lib/utils";
import Toast from "@/lib/components/Toast";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { BackgroundMusicDto } from "@repo/api/background-music";
import {
  IconCheck,
  IconDotsVertical,
  IconHeart,
  IconHeartFilled,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconSearch,
  IconWaveSine,
} from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import WaveformPlayer from "../VoiceoverTab/WaveformPlayer";
import DeleteTrackDialog from "./DeleteTrackDialog";
import {
  useBackgroundMusicsByExtIdsQuery,
  useToggleBackgroundMusicFavoriteMutation,
} from "@/lib/queries/background-music-queries";

type TrackListViewProps = {
  tracks: BackgroundMusicDto[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  previewTrack: BackgroundMusicDto | null;
  isPreviewPlaying: boolean;
  setIsPreviewPlaying: (isPlaying: boolean) => void;
  onPreviewPlayPause: (track: BackgroundMusicDto) => void;
  onEditTrack: (externalId: string) => void;
  previewDurationMs: number;
  videoIsPlaying: boolean;
  isFavoritesTab?: boolean;
  showFavoritesEmptyState?: boolean;
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
};

function TrackListView({
  tracks,
  searchQuery,
  onSearchChange,
  previewTrack,
  isPreviewPlaying,
  setIsPreviewPlaying,
  onPreviewPlayPause,
  onEditTrack,
  previewDurationMs,
  videoIsPlaying,
  isFavoritesTab,
  showFavoritesEmptyState,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: TrackListViewProps) {
  return (
    <>
      <SearchBar searchQuery={searchQuery} onSearchChange={onSearchChange} />
      <TrackList
        tracks={tracks}
        previewTrackId={previewTrack?.externalId ?? null}
        isPreviewPlaying={isPreviewPlaying}
        onPreviewPlayPause={onPreviewPlayPause}
        onEditTrack={onEditTrack}
        isFavoritesTab={isFavoritesTab}
        showFavoritesEmptyState={showFavoritesEmptyState}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={onLoadMore}
      />
      <PreviewFooter
        previewTrack={previewTrack}
        isPreviewPlaying={isPreviewPlaying}
        setIsPreviewPlaying={setIsPreviewPlaying}
        durationMs={previewDurationMs}
        videoIsPlaying={videoIsPlaying}
      />
    </>
  );
}

type TrackListProps = {
  tracks: BackgroundMusicDto[];
  previewTrackId: string | null;
  isPreviewPlaying: boolean;
  onPreviewPlayPause: (track: BackgroundMusicDto) => void;
  onEditTrack: (externalId: string) => void;
  isFavoritesTab?: boolean;
  showFavoritesEmptyState?: boolean;
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
};

function TrackList({
  tracks,
  previewTrackId,
  isPreviewPlaying,
  onPreviewPlayPause,
  onEditTrack,
  isFavoritesTab,
  showFavoritesEmptyState,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: TrackListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: tracks.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60,
    overscan: 8,
    getItemKey: (index) => tracks[index]!.externalId,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;
    if (
      lastItem.index >= tracks.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      onLoadMore();
    }
  }, [
    virtualItems,
    tracks.length,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  ]);

  if (isLoading && tracks.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LoadingSpinner size={24} />
      </div>
    );
  }

  if (tracks.length === 0) {
    if (showFavoritesEmptyState) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center px-8 py-12">
          <IconHeart
            className="mb-3 text-text-primary"
            size={32}
            stroke={1.5}
          />
          <p className="mb-0.5 text-sm font-medium text-text-secondary">
            No favourite tracks yet
          </p>
          <p className="text-sm text-text-secondary">
            Favourite a track from its menu to see it here
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-1 flex-col items-center justify-center px-8 py-12">
        <IconSearch className="mb-3 text-text-primary" size={32} stroke={1.5} />
        <p className="mb-0.5 text-sm font-medium text-text-secondary">
          No tracks found
        </p>
        <p className="text-sm text-text-secondary">
          Try adjusting your search or filters
        </p>
      </div>
    );
  }

  return (
    <div className="relative grow overflow-hidden">
      <div
        ref={parentRef}
        className="absolute inset-0 overflow-y-auto px-8 py-3 pb-8"
      >
        <div
          className="relative w-full"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualItems.map((virtualItem) => {
            const item = tracks[virtualItem.index]!;
            const isCurrentPreview = previewTrackId === item.externalId;
            const isPlaying = isCurrentPreview && isPreviewPlaying;

            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                className="absolute top-0 left-0 w-full pb-1"
                style={{ transform: `translateY(${virtualItem.start}px)` }}
              >
                <Track
                  {...item}
                  track={item}
                  isPlaying={isPlaying}
                  onPlayPause={() => onPreviewPlayPause(item)}
                  onEditTrack={onEditTrack}
                  isFavoritesTab={isFavoritesTab}
                />
              </div>
            );
          })}
        </div>
        {isFetchingNextPage && (
          <div className="flex justify-center py-4">
            <LoadingSpinner size={20} />
          </div>
        )}
      </div>
    </div>
  );
}

function Track({
  externalId,
  name,
  isPlaying,
  onPlayPause,
  userAdded,
  isFavorite,
  hasBeatSync,
  metadata,
  onEditTrack,
  isFavoritesTab,
  track,
}: BackgroundMusicDto & {
  isPlaying: boolean;
  onPlayPause: () => void;
  onEditTrack: (externalId: string) => void;
  isFavoritesTab?: boolean;
  track: BackgroundMusicDto;
}) {
  const isSelected = useEditorStore(
    (state) =>
      state.video?.compositionJson.backgroundMusic?.externalId === externalId,
  );

  const filters = metadata
    ? [...metadata.genres, ...metadata.moods, ...metadata.tags]
    : [];

  return (
    <div className="group relative -mx-3 flex items-center gap-3 rounded-lg px-3 py-2 transition-colors duration-100 hover:bg-grey-100">
      <Button
        variant="outline"
        size="iconSm"
        aria-label={isPlaying ? `Pause ${name}` : `Play ${name}`}
        aria-pressed={isPlaying}
        onClick={onPlayPause}
      >
        {isPlaying ? <IconPlayerPauseFilled /> : <IconPlayerPlayFilled />}
      </Button>
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden text-left">
        <span className="truncate text-sm font-medium text-text-primary">
          {name}
        </span>
        {filters.length > 0 && (
          <span className="truncate text-xs text-text-secondary">
            {filters.join(", ")}
          </span>
        )}
      </div>
      <TrackSettings
        externalId={externalId}
        name={name}
        userAdded={userAdded}
        isFavorite={isFavorite}
        hasBeatSync={hasBeatSync}
        isSelected={isSelected}
        onEditTrack={onEditTrack}
        isFavoritesTab={isFavoritesTab}
        track={track}
      />
    </div>
  );
}

function TrackSettings({
  externalId,
  name,
  userAdded,
  isFavorite,
  hasBeatSync,
  isSelected,
  onEditTrack,
  isFavoritesTab,
  track,
}: {
  externalId: string;
  name: string;
  userAdded: boolean;
  isFavorite: boolean;
  hasBeatSync: boolean;
  isSelected: boolean;
  onEditTrack: (externalId: string) => void;
  isFavoritesTab?: boolean;
  track: BackgroundMusicDto;
}) {
  const selectBackgroundMusic = useEditorStore(
    (state) => state.service.selectBackgroundMusic,
  );
  const setBackgroundMusicView = useEditorStore(
    (state) => state.service.setBackgroundMusicView,
  );
  const setBackgroundMusicFavorite = useEditorStore(
    (state) => state.service.setBackgroundMusicFavorite,
  );
  const toggleBackgroundMusicFavorite =
    useToggleBackgroundMusicFavoriteMutation(
      useEditorStore((state) => state.teamExternalId),
    );
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUnfavoriteDialogOpen, setIsUnfavoriteDialogOpen] = useState(false);

  const handleSelectTrack = () => {
    selectBackgroundMusic(track);
    setBackgroundMusicView("HIDDEN");
  };

  const handleEditTrack = () => {
    setIsMenuOpen(false);
    onEditTrack(externalId);
  };

  const toggleFavorite = () => {
    setBackgroundMusicFavorite(externalId, !isFavorite);
    toggleBackgroundMusicFavorite.mutate(externalId, {
      onError: () => setBackgroundMusicFavorite(externalId, isFavorite),
    });
  };

  const handleToggleFavorite = () => {
    if (isFavoritesTab && isFavorite) {
      setIsUnfavoriteDialogOpen(true);
      return;
    }
    toggleFavorite();
    Toast.info(isFavorite ? "Removed from favourites" : "Added to favourites");
  };

  const handleConfirmUnfavorite = () => {
    toggleFavorite();
    Toast.info("Removed from favourites");
    setIsUnfavoriteDialogOpen(false);
  };

  const showActionsClass = cn(
    "hidden group-focus-within:inline-flex group-hover:inline-flex",
    isMenuOpen && "inline-flex",
  );

  return (
    <>
      <div className="flex items-center gap-2">
        {!isMenuOpen && (
          <>
            {hasBeatSync ? (
              <Badge
                variant="outline"
                className="gap-1 bg-white group-focus-within:hidden group-hover:hidden"
              >
                <IconWaveSine size={14} />
                Beat sync
              </Badge>
            ) : null}
            {userAdded ? (
              <Badge
                variant="outline"
                className="bg-white group-focus-within:hidden group-hover:hidden"
              >
                Team track
              </Badge>
            ) : null}
            {isSelected ? (
              <span
                className="mx-2 flex items-center justify-center group-focus-within:hidden group-hover:hidden [&_svg]:stroke-[url(#icon-gradient)]"
                aria-label="Selected"
              >
                <IconCheck size={20} stroke={2.5} />
              </span>
            ) : null}
          </>
        )}
        <FavoriteToggleButton
          isFavorite={isFavorite}
          onToggle={handleToggleFavorite}
          className={showActionsClass}
        />
        {userAdded && (
          <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="iconSm"
                className={showActionsClass}
                aria-label="Track actions"
              >
                <IconDotsVertical />
              </Button>
            </DropdownMenuTrigger>
            {isMenuOpen && (
              <DropdownMenuContent
                className="w-40"
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <DropdownMenuItem onSelect={handleEditTrack}>
                  Edit track
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-error hover:text-error!"
                  onSelect={() => setIsDeleteDialogOpen(true)}
                >
                  Delete track
                </DropdownMenuItem>
              </DropdownMenuContent>
            )}
          </DropdownMenu>
        )}
        <Button
          variant="outline"
          size="sm"
          className={showActionsClass}
          onClick={handleSelectTrack}
        >
          Use
        </Button>
      </div>
      {userAdded && (
        <DeleteTrackDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          trackExternalId={externalId}
          trackName={name}
        />
      )}
      <UnfavoriteTrackDialog
        open={isUnfavoriteDialogOpen}
        onOpenChange={setIsUnfavoriteDialogOpen}
        trackName={name}
        onConfirm={handleConfirmUnfavorite}
      />
    </>
  );
}

function UnfavoriteTrackDialog({
  open,
  onOpenChange,
  trackName,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  trackName: string;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="w-full">
          <DialogTitle>Remove favourite</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove{" "}
            <span className="font-semibold">{trackName}</span> from your
            favourites?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={onConfirm}>Remove</Button>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FavoriteToggleButton({
  isFavorite,
  onToggle,
  className,
}: {
  isFavorite: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="outline"
      size="iconSm"
      className={className}
      onClick={(e) => {
        // Blur on mouse activation so the row doesn't stay visible after the
        // pointer leaves; keyboard activation (detail === 0) keeps focus.
        if (e.detail !== 0) {
          e.currentTarget.blur();
        }
        onToggle();
      }}
      aria-label={isFavorite ? "Remove favourite" : "Add to favourites"}
    >
      {isFavorite ? (
        <IconHeartFilled size={18} className="text-error" />
      ) : (
        <IconHeart size={18} className="text-text-primary" />
      )}
    </Button>
  );
}

function SearchBar({
  searchQuery,
  onSearchChange,
}: {
  searchQuery: string;
  onSearchChange: (query: string) => void;
}) {
  return (
    <div className="mt-4 px-8">
      <div className="relative">
        <IconSearch
          className="absolute top-1/2 left-3.5 -translate-y-1/2 text-grey-400"
          size={16}
          stroke={2.5}
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search tracks..."
          className="border-grey-300 w-full rounded-md border border-grey-150 bg-grey-100 px-4 py-2 pl-10 text-sm font-medium text-grey-400 placeholder:text-text-secondary focus-visible:outline-tertiary-400"
        />
      </div>
    </div>
  );
}

function PreviewFooter({
  previewTrack,
  isPreviewPlaying,
  setIsPreviewPlaying,
  durationMs,
  videoIsPlaying,
}: {
  previewTrack: BackgroundMusicDto | null;
  isPreviewPlaying: boolean;
  setIsPreviewPlaying: (isPlaying: boolean) => void;
  durationMs: number;
  videoIsPlaying: boolean;
}) {
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const { data: previewTracksWithPeaks = [] } =
    useBackgroundMusicsByExtIdsQuery(
      teamExternalId,
      previewTrack ? [previewTrack.externalId] : [],
    );
  const peaks = previewTracksWithPeaks[0]?.peaks ?? undefined;

  if (!previewTrack) {
    return null;
  }

  return (
    <div className="z-10 border-t border-grey-200 px-8 py-6">
      <div className="flex h-4 items-center gap-2">
        <h3 className="mx-1.5 truncate text-sm font-semibold text-text-primary">
          {previewTrack.name}
        </h3>
      </div>
      {previewTrack.src && (
        <WaveformPlayer
          url={previewTrack.src}
          peaks={peaks}
          durationMs={durationMs}
          isPlaying={isPreviewPlaying}
          onPlay={() => setIsPreviewPlaying(true)}
          onPause={() => setIsPreviewPlaying(false)}
          videoIsPlaying={videoIsPlaying}
        />
      )}
    </div>
  );
}

export default TrackListView;
